/**
 * Logged-out homepage nav + hero through How it Works, with the hero + the section
 * right below it chosen by the landing-home-v2 flag: classic (astronaut hero +
 * Mission) or v2 (gradient hero with capability cards + Training + company
 * logo strip). In v2, Training's topic chips and How it Works' "Build with
 * Lev" buttons send a starter into the hero's chat.
 *
 * Client-gated on purpose: a server-side evaluation needs a PostHog distinct
 * id that a first-time visitor does not have yet, which would split one
 * visitor across both arms.
 */
"use client";

import { useCallback, useEffect, useState } from "react";
import LandingHero, { type LandingHeroProps } from "@/components/organisms/landing/LandingHero";
import { LandingMission } from "@/components/organisms/landing/LandingMission";
import { LandingNav, type LandingNavProps } from "@/components/organisms/landing/LandingNav";
import { LandingHowItWorks } from "@/components/organisms/landing/LandingHowItWorks";
import {
  LandingTraining,
  type LandingTrainingTopic,
} from "@/components/organisms/landing/LandingTraining";
import { LandingLogoStrip } from "@/components/organisms/landing/LandingLogoStrip";
import type { LandingStarterSource } from "@/components/molecules/landing/IdeaChatInput";
import { trackLandingTrainingTopicClicked } from "@/lib/analytics/client";
import {
  FEATURE_FLAGS,
  useFeatureFlag,
  useFeatureFlagLoading,
} from "@/lib/analytics/featureFlags";

const BUILD_WITH_LEV_STARTER = "I want to build my startup with Lev.";

export interface LandingHomeSectionsProps {
  firstName?: string;
  existingAnonProjectId?: string;
  /** Rendered here so the nav label follows the same locked variant as the sections. */
  nav?: Pick<LandingNavProps, "isLoggedIn" | "userInitials" | "userAvatarUrl">;
  /** Passed to LandingHero, which accepts these overrides for Storybook/tests. */
  heroOverrides?: Pick<LandingHeroProps, "useUserImpl" | "useRouterImpl" | "onNavigate">;
}

export function LandingHomeSections({
  firstName,
  existingAnonProjectId,
  nav = { isLoggedIn: false },
  heroOverrides,
}: LandingHomeSectionsProps) {
  const flagOn = useFeatureFlag(FEATURE_FLAGS.LANDING_HOME_V2);
  const flagLoading = useFeatureFlagLoading(FEATURE_FLAGS.LANDING_HOME_V2);
  const [mounted, setMounted] = useState(false);
  // Locked at the first settled value: a later flag reload must not swap the
  // hero, because that remounts IdeaChatInput mid-conversation.
  const [variant, setVariant] = useState<"classic" | "v2" | null>(null);
  const [heroSendRequest, setHeroSendRequest] = useState<{
    text: string;
    nonce: number;
    source: LandingStarterSource;
  }>();
  const sendToHeroChat = useCallback((text: string, source: LandingStarterSource) => {
    setHeroSendRequest((prev) => ({ text, nonce: (prev?.nonce ?? 0) + 1, source }));
  }, []);
  const handleTopicSelect = useCallback(
    (topic: LandingTrainingTopic) => {
      trackLandingTrainingTopicClicked({ topic: topic.id });
      sendToHeroChat(topic.starter, "training_topic");
    },
    [sendToHeroChat],
  );
  const handleBuildWithLev = useCallback(
    () => sendToHeroChat(BUILD_WITH_LEV_STARTER, "how_it_works_cta"),
    [sendToHeroChat],
  );

  // SSR has no flag values, so the first client render must match it (pending)
  // even when posthog-js already has cached flags.
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!mounted || flagLoading || variant !== null) return;
    setVariant(flagOn ? "v2" : "classic");
  }, [mounted, flagLoading, flagOn, variant]);

  // No chat renders behind the resume notice, so v2's chat triggers fall back
  // to their static/scroll-to-top behavior there.
  const heroChatAvailable = variant === "v2" && !existingAnonProjectId;

  return (
    <>
      <LandingNav
        {...nav}
        howItWorksLabel={variant === "v2" ? "How Lev Works" : undefined}
      />
      {variant === null ? (
        // Holds the classic hero's height (the production default) so the page
        // below does not jump for most visitors when the flag settles.
        <div className="min-h-[85vh] md:min-h-screen" aria-hidden="true" />
      ) : (
        <>
          <LandingHero
            {...heroOverrides}
            firstName={firstName}
            landingPageSlug="home"
            existingAnonProjectId={existingAnonProjectId}
            variant={variant}
            externalSendRequest={heroSendRequest}
          />
          {variant === "v2" ? (
            <>
              <LandingTraining
                onTopicSelect={heroChatAvailable ? handleTopicSelect : undefined}
              />
              <LandingLogoStrip />
            </>
          ) : (
            <LandingMission />
          )}
        </>
      )}
      <LandingHowItWorks
        onBuildWithLev={heroChatAvailable ? handleBuildWithLev : undefined}
        {...(variant === "v2" && {
          title: "How Lev Works",
          subtitle: "Lev brings everything a founding team does into one place.",
        })}
      />
    </>
  );
}
